/**
 * MediaPlayer — shared <audio>/<video> player with chrome.
 *
 * Extracted from MultimediaApp so MaterialSlide can reuse the same UI.
 * Self-contained:
 * - Owns currentTime / duration / isPlaying / volume / isMuted / isFullscreen
 * - Owns the <audio> or <video> element + its ref
 * - Wires standard HTMLMediaElement events (timeupdate, durationchange,
 *   ended, loadedmetadata, error)
 * - Exposes those as callback props so the parent can react (auto-next
 *   track, persist progress, mark-complete, etc.)
 *
 * Visual design intentionally matches the existing MultimediaApp player
 * so consumers get the same look across the desktop.
 */

import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Music,
  Maximize,
  Minimize,
} from 'lucide-react'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/lib/utils'

export interface MediaPlayerProps {
  /** Resolved media URL (blob:, https:, etc.) ready for the browser to play. */
  src: string
  type: 'audio' | 'video'
  /** Title shown on the audio "card" (ignored for video). */
  title?: string
  /** Resume position in seconds. */
  initialTime?: number
  /** Initial volume 0..1. Defaults to 1. */
  defaultVolume?: number
  /** Auto-start playback once metadata is loaded. */
  autoplay?: boolean
  /**
   * Known duration in seconds — used as a fallback when the media element's
   * own metadata lacks it. WebM blobs recorded via MediaRecorder report no
   * duration until seeked, so callers that already know it (e.g. a recording's
   * stored duration) can pass it here.
   */
  fallbackDuration?: number
  /** Outer className override (lets the parent constrain dimensions). */
  className?: string
  /**
   * Jump to `t` seconds. A new `nonce` repeats the jump even to the same time
   * (a second click on the same timecode must seek again).
   */
  seekRequest?: { t: number; nonce: number }
  /** Poster image for video (object or same-origin URL). */
  poster?: string
  /** WebVTT caption tracks for video. */
  captions?: { src: string; srclang: string; label: string }[]
  /** Accessible name of the video region (defaults to "Video player"). */
  ariaLabel?: string

  // Event callbacks — all optional so the parent picks what to react to.
  onTimeUpdate?: (currentTime: number) => void
  onLoadedMetadata?: (duration: number) => void
  onEnded?: () => void
  onPlay?: () => void
  onPause?: () => void
  onError?: (error: unknown) => void
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function MediaPlayer({
  src,
  type,
  title,
  initialTime = 0,
  defaultVolume = 1,
  autoplay = false,
  fallbackDuration,
  className,
  seekRequest,
  poster,
  captions,
  ariaLabel,
  onTimeUpdate,
  onLoadedMetadata,
  onEnded,
  onPlay,
  onPause,
  onError,
}: MediaPlayerProps) {
  const { t } = useTranslation()
  const audioRef = useRef<HTMLAudioElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(defaultVolume)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(false)
  // While the WebM duration fix below is seeking to the end, a requested jump
  // waits here and is applied instead of the fix's reset to 0 — otherwise the
  // fix would throw the jump away.
  const durationFixPendingRef = useRef(false)
  const pendingSeekRef = useRef<number | null>(null)

  const mediaEl = (): HTMLMediaElement | null =>
    type === 'video' ? videoRef.current : audioRef.current

  // Wire HTMLMediaElement events to local state and parent callbacks.
  useEffect(() => {
    const m = mediaEl()
    if (!m) return

    const onTime = () => {
      setCurrentTime(m.currentTime)
      onTimeUpdate?.(m.currentTime)
    }
    const onDur = () => {
      if (isFinite(m.duration) && m.duration > 0) {
        setDuration(m.duration)
        onLoadedMetadata?.(m.duration)
      } else if (fallbackDuration && fallbackDuration > 0) {
        // Element has no usable duration yet (WebM blob) — show the caller's
        // known value so the scrubber stays usable.
        setDuration(fallbackDuration)
      }
    }
    const onEnd = () => {
      setIsPlaying(false)
      onEnded?.()
    }
    const onPlayEvt = () => {
      setIsPlaying(true)
      onPlay?.()
    }
    const onPauseEvt = () => {
      setIsPlaying(false)
      onPause?.()
    }
    const onErrEvt = () => onError?.(m.error ?? new Error('media error'))

    m.addEventListener('timeupdate', onTime)
    m.addEventListener('durationchange', onDur)
    m.addEventListener('loadedmetadata', onDur)
    m.addEventListener('ended', onEnd)
    m.addEventListener('play', onPlayEvt)
    m.addEventListener('pause', onPauseEvt)
    m.addEventListener('error', onErrEvt)

    return () => {
      m.removeEventListener('timeupdate', onTime)
      m.removeEventListener('durationchange', onDur)
      m.removeEventListener('loadedmetadata', onDur)
      m.removeEventListener('ended', onEnd)
      m.removeEventListener('play', onPlayEvt)
      m.removeEventListener('pause', onPauseEvt)
      m.removeEventListener('error', onErrEvt)
    }
    // type drives which ref we wire to. The other callback identities are
    // intentionally NOT in deps — they would re-register every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, src])

  // Apply volume / mute to the actual media element.
  useEffect(() => {
    const m = mediaEl()
    if (!m) return
    m.volume = isMuted ? 0 : volume
    m.muted = isMuted
  }, [volume, isMuted, type])

  // Seed duration from the caller's known value so the scrubber is usable
  // before (or instead of) the element reporting its own metadata.
  useEffect(() => {
    if (fallbackDuration && fallbackDuration > 0) {
      setDuration((d) => (d > 0 ? d : fallbackDuration))
    }
  }, [fallbackDuration])

  // WebM blobs recorded via MediaRecorder report duration=Infinity until the
  // element is seeked past the end. Force the browser to compute the real
  // duration, then reset to the start.
  useEffect(() => {
    const m = mediaEl()
    if (!m) return
    const fix = () => {
      if (isFinite(m.duration) && m.duration > 0) return
      durationFixPendingRef.current = true
      const positionBefore = m.currentTime
      const onSeeked = () => {
        m.removeEventListener('seeked', onSeeked)
        durationFixPendingRef.current = false
        if (isFinite(m.duration) && m.duration > 0) setDuration(m.duration)
        m.currentTime = pendingSeekRef.current ?? positionBefore
        pendingSeekRef.current = null
      }
      m.addEventListener('seeked', onSeeked)
      m.currentTime = 1e10
    }
    m.addEventListener('loadedmetadata', fix, { once: true })
    return () => m.removeEventListener('loadedmetadata', fix)
  }, [src, type])

  // Seek once metadata is loaded; while the WebM fix runs, hand the target
  // to it so its reset lands on the target instead of 0.
  const seekWhenReady = (m: HTMLMediaElement, target: number): (() => void) => {
    const apply = () => {
      if (durationFixPendingRef.current) {
        pendingSeekRef.current = target
        return
      }
      const end = isFinite(m.duration) && m.duration > 0 ? m.duration : target
      m.currentTime = Math.max(0, Math.min(target, end))
      setCurrentTime(m.currentTime)
    }
    if (m.readyState >= 1 && m.duration > 0) {
      apply()
      return () => {}
    }
    m.addEventListener('loadedmetadata', apply, { once: true })
    return () => m.removeEventListener('loadedmetadata', apply)
  }

  // Seek to initialTime once metadata is loaded.
  useEffect(() => {
    const m = mediaEl()
    if (!m || initialTime <= 0) return
    return seekWhenReady(m, initialTime)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, initialTime, type])

  // Jump requests from outside (timecode links, source cards).
  useEffect(() => {
    const m = mediaEl()
    if (!m || !seekRequest || !(seekRequest.t >= 0)) return
    return seekWhenReady(m, seekRequest.t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, type, seekRequest?.t, seekRequest?.nonce])

  // Sync isFullscreen with the document.
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])

  // Autoplay once src is in place.
  useEffect(() => {
    if (!autoplay) return
    const m = mediaEl()
    if (!m) return
    const tryPlay = () => {
      void m.play().catch(() => {
        // Browsers may reject autoplay without user gesture — silent fallback.
      })
    }
    if (m.readyState >= 2) {
      tryPlay()
    } else {
      m.addEventListener('canplay', tryPlay, { once: true })
      return () => m.removeEventListener('canplay', tryPlay)
    }
  }, [src, autoplay, type])

  const togglePlay = () => {
    const m = mediaEl()
    if (!m) return
    if (m.paused) {
      void m.play().catch(() => {})
    } else {
      m.pause()
    }
  }

  const toggleMute = () => setIsMuted((v) => !v)

  const handleSeek = (vals: number[]) => {
    const m = mediaEl()
    if (!m) return
    const t = vals[0] ?? 0
    m.currentTime = t
    setCurrentTime(t)
  }

  const handleVolume = (vals: number[]) => {
    const v = vals[0] ?? 0
    setVolume(v)
    if (isMuted && v > 0) setIsMuted(false)
  }

  const skipBy = (delta: number) => {
    const m = mediaEl()
    if (!m) return
    const end = isFinite(m.duration) && m.duration > 0 ? m.duration : duration
    m.currentTime = Math.max(0, Math.min(m.currentTime + delta, end || m.currentTime + delta))
    setCurrentTime(m.currentTime)
  }

  // Keyboard control of the video region: Space/K play-pause, ←/→ five
  // seconds, M mute, F fullscreen. Keys on the inner buttons and the slider
  // keep their own meaning.
  const handleVideoKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    switch (e.key) {
      case ' ':
      case 'k':
      case 'K':
        e.preventDefault()
        togglePlay()
        break
      case 'ArrowLeft':
        e.preventDefault()
        skipBy(-5)
        break
      case 'ArrowRight':
        e.preventDefault()
        skipBy(5)
        break
      case 'm':
      case 'M':
        e.preventDefault()
        toggleMute()
        break
      case 'f':
      case 'F':
        e.preventDefault()
        toggleFullscreen()
        break
    }
  }

  const toggleFullscreen = () => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) {
      void containerRef.current.requestFullscreen().catch(() => {})
    } else {
      void document.exitFullscreen().catch(() => {})
    }
  }

  // ---------------------------------------------------------------------------
  // VIDEO
  // ---------------------------------------------------------------------------
  if (type === 'video') {
    return (
      <div
        ref={containerRef}
        role="region"
        aria-label={ariaLabel ?? t('video.player.region')}
        aria-keyshortcuts="Space K ArrowLeft ArrowRight M F"
        tabIndex={0}
        className={cn(
          'relative w-full h-full group bg-black rounded-lg shadow-lg flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]',
          className,
        )}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
        onKeyDown={handleVideoKeyDown}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className={cn(
            isFullscreen
              ? 'w-full h-full object-cover'
              : 'max-w-full max-h-full object-contain',
          )}
          controls={false}
          preload="metadata"
          playsInline
          onClick={togglePlay}
        >
          {captions?.map((track, i) => (
            <track
              key={track.src}
              kind="captions"
              src={track.src}
              srcLang={track.srclang}
              label={track.label}
              default={i === 0}
            />
          ))}
        </video>

        {/* Hover/focus overlay (controls + center play). Keyboard users get it
            while focus is inside the player; paused video keeps it visible. */}
        <div
          className={cn(
            'absolute inset-0 transition-opacity duration-300 motion-reduce:transition-none group-focus-within:opacity-100',
            showControls || !isPlaying ? 'opacity-100' : 'opacity-0',
          )}
        >
          {/* Center play/pause */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <button
              type="button"
              onClick={togglePlay}
              className="pointer-events-auto bg-black/30 hover:bg-black/50 text-white rounded-full p-3 transition-all duration-200 hover:scale-110 motion-reduce:transition-none motion-reduce:hover:scale-100 backdrop-blur-sm"
              aria-label={isPlaying ? t('video.player.pause') : t('video.player.play')}
            >
              {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
            </button>
          </div>

          {/* Bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={togglePlay}
                  className="text-white hover:text-white/80 transition-colors p-1"
                  style={{ color: 'white' }}
                  aria-label={isPlaying ? t('video.player.pause') : t('video.player.play')}
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5" style={{ color: 'white' }} />
                  ) : (
                    <Play className="h-5 w-5" style={{ color: 'white' }} />
                  )}
                </button>
                <div className="text-sm font-mono" style={{ color: 'white' }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="transition-colors p-1"
                  style={{ color: 'white' }}
                  aria-label={isMuted ? t('video.player.unmute') : t('video.player.mute')}
                >
                  {isMuted ? (
                    <VolumeX className="h-5 w-5" style={{ color: 'white' }} />
                  ) : (
                    <Volume2 className="h-5 w-5" style={{ color: 'white' }} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="transition-colors p-1"
                  style={{ color: 'white' }}
                  aria-label={isFullscreen ? t('video.player.exitFullscreen') : t('video.player.fullscreen')}
                >
                  {isFullscreen ? (
                    <Minimize className="h-5 w-5" style={{ color: 'white' }} />
                  ) : (
                    <Maximize className="h-5 w-5" style={{ color: 'white' }} />
                  )}
                </button>
              </div>
            </div>

            <div className="w-full cursor-pointer">
              <Slider
                aria-label={t('video.player.seek')}
                value={[currentTime]}
                max={duration || 0}
                step={1}
                onValueChange={handleSeek}
                variant="overlay"
                className="w-full cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // AUDIO
  // ---------------------------------------------------------------------------
  return (
    <div className={cn('w-full flex flex-col items-center justify-center', className)}>
      {/* Audio "card" */}
      <div className="flex items-center justify-center w-full mb-8">
        <div className="w-24 h-24 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center">
          <Music className="h-12 w-12 text-primary/50" />
        </div>
      </div>
      {title && (
        <h4 className="text-base font-medium text-[var(--text-color)] mb-4 text-center">
          {title}
        </h4>
      )}

      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              className="text-[var(--text-color)] hover:text-[var(--text-color)]/80 transition-colors p-1"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <div className="text-sm font-mono text-[var(--text-secondary)]">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleMute}
              className="text-[var(--text-color)]/80 hover:text-[var(--text-color)] transition-colors p-1"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="w-16">
              <Slider
                value={[volume]}
                max={1}
                step={0.01}
                onValueChange={handleVolume}
                aria-label={t('video.player.volume')}
                className="w-full cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div className="w-full cursor-pointer">
          <Slider
            value={[currentTime]}
            max={duration || 0}
            step={1}
            onValueChange={handleSeek}
            aria-label={t('video.player.seek')}
            className="w-full cursor-pointer"
          />
        </div>
      </div>

      {/* Hidden <audio> — actual playback element. */}
      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  )
}
